'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

type TelegramEnvContextValue = {
  isTelegramWebApp: boolean;
  isTWA: boolean;
};

const TelegramEnvContext = createContext<TelegramEnvContextValue>({
  isTelegramWebApp: false,
  isTWA: false,
});

type TelegramProviderProps = {
  children: React.ReactNode;
};

function readTelegramFlags() {
  if (typeof window === 'undefined') {
    return { isTelegramWebApp: false, isTWA: false };
  }
  const telegramWebApp = (window as Window & { Telegram?: { WebApp?: { initData?: string } } })
    .Telegram?.WebApp;
  const isTelegramWebApp = Boolean(telegramWebApp);
  const isTWA = Boolean(telegramWebApp?.initData);
  return { isTelegramWebApp, isTWA };
}

export function TelegramProvider({ children }: TelegramProviderProps) {
  const [flags, setFlags] = useState(readTelegramFlags);
  const hasTwaAuthAttempt = useRef(false);

  useEffect(() => {
    setFlags(readTelegramFlags());
  }, []);

  useEffect(() => {
    if (!flags.isTWA || hasTwaAuthAttempt.current) return;
    hasTwaAuthAttempt.current = true;
  }, [flags.isTWA]);

  const value = useMemo(
    () => ({
      isTelegramWebApp: flags.isTelegramWebApp,
      isTWA: flags.isTWA,
    }),
    [flags],
  );

  return <TelegramEnvContext.Provider value={value}>{children}</TelegramEnvContext.Provider>;
}

export function useTelegramEnv() {
  return useContext(TelegramEnvContext);
}
